import { useState } from 'react';
import { formatDate } from '../utils/timezone';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePickingList,
  useStartPicking,
  useCompletePicking,
  useCancelPicking,
  useConfirmPick,
  useReportShortage,
  useSkipPickItem,
} from '../hooks/useWmsPicking';
import { useCreatePackingSlipFromWorkOrder } from '../hooks/useWmsShipping';
import { PickingListStatusBadge } from '../components/picking/PickingStatusBadge';
import { PickingItemsTable } from '../components/picking/PickingItemsTable';
import { ConfirmPickModal } from '../components/picking/ConfirmPickModal';
import { ReportShortageModal } from '../components/picking/ReportShortageModal';
import { PromptDialog } from '../components/shared/ConfirmDialog';
import type {
  PickingList,
  PickingListItem,
  ConfirmPickInput,
  ReportShortageInput,
} from '../types';

export default function PickingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Modal state
  const [confirmPickItem, setConfirmPickItem] = useState<PickingListItem | null>(null);
  const [shortageItem, setShortageItem] = useState<PickingListItem | null>(null);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);

  // Queries
  const pickingListQuery = usePickingList(id || '');

  // Mutations
  const startMutation = useStartPicking();
  const completeMutation = useCompletePicking();
  const cancelMutation = useCancelPicking();
  const confirmPickMutation = useConfirmPick();
  const reportShortageMutation = useReportShortage();
  const skipItemMutation = useSkipPickItem();
  const createPackingSlipMutation = useCreatePackingSlipFromWorkOrder();

  const pickingList = pickingListQuery.data as PickingList | undefined;
  const items = pickingList?.items || [];

  const handleStart = async () => {
    if (!id) return;
    await startMutation.mutateAsync(id);
  };

  const handleComplete = async () => {
    if (!id) return;
    await completeMutation.mutateAsync(id);
  };

  const handleCancel = async (reason?: string) => {
    if (!id) return;
    await cancelMutation.mutateAsync({ pickingListId: id, reason: reason || undefined });
  };

  const handleConfirmPick = async (data: ConfirmPickInput) => {
    if (!id || !confirmPickItem) return;
    await confirmPickMutation.mutateAsync({
      pickingListId: id,
      itemId: confirmPickItem.id,
      data,
    });
  };

  const handleReportShortage = async (data: ReportShortageInput) => {
    if (!id || !shortageItem) return;
    await reportShortageMutation.mutateAsync({
      pickingListId: id,
      itemId: shortageItem.id,
      data,
    });
  };

  const handleSkip = async (item: PickingListItem) => {
    if (!id) return;
    await skipItemMutation.mutateAsync({
      pickingListId: id,
      itemId: item.id,
    });
  };

  const handleCreatePackingSlip = async () => {
    if (!pickingList?.work_order_id) return;
    const result = await createPackingSlipMutation.mutateAsync(pickingList.work_order_id);
    // Navigate to the new packing slip
    navigate(`/wms/shipping/${result.id}`);
  };

  // formatDate imported from utils/timezone (Montreal timezone)
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return formatDate(dateStr, 'datetime');
  };

  if (pickingListQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (pickingListQuery.error || !pickingList) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Picking List not found</h2>
        <p className="text-muted-foreground mt-2">
          The picking list you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/wms/picking">Back to Picking Lists</Link>
        </Button>
      </div>
    );
  }

  const totalItems = pickingList.item_count ?? 0;
  const itemsPicked = pickingList.items_picked ?? 0;
  const progress = totalItems > 0
    ? Math.round((itemsPicked / totalItems) * 100)
    : 0;

  const canPick = pickingList.status === 'in_progress';
  const canStart = pickingList.status === 'pending' || pickingList.status === 'assigned';
  const canComplete = pickingList.status === 'in_progress' && itemsPicked > 0;
  const canCancel = pickingList.status !== 'completed' && pickingList.status !== 'cancelled';
  const canCreatePackingSlip = pickingList.status === 'completed' && pickingList.work_order_id;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/wms/picking"
              className="text-muted-foreground hover:text-foreground"
            >
              Picking Lists
            </Link>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-2xl font-bold tracking-tight font-mono">
              {pickingList.picking_number}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Work Order: {pickingList.work_order_number || '—'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {canStart && (
            <Button onClick={handleStart} disabled={startMutation.isPending}>
              {startMutation.isPending ? 'Starting...' : 'Start Picking'}
            </Button>
          )}
          {canComplete && (
            <Button onClick={handleComplete} disabled={completeMutation.isPending}>
              {completeMutation.isPending ? 'Completing...' : 'Complete'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              className="text-red-600"
              onClick={() => setShowCancelPrompt(true)}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
          {canCreatePackingSlip && (
            <Button
              onClick={handleCreatePackingSlip}
              disabled={createPackingSlipMutation.isPending}
            >
              {createPackingSlipMutation.isPending ? 'Creating...' : 'Create Packing Slip'}
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PickingListStatusBadge status={pickingList.status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Assigned To
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{pickingList.assigned_to_name || '—'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm font-medium">
                {itemsPicked}/{totalItems}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span>{formatDate(pickingList.created_at)}</span>
            </div>
            {pickingList.started_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span>{formatDateTime(pickingList.started_at)}</span>
              </div>
            )}
            {pickingList.completed_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Completed:</span>
                <span>{formatDateTime(pickingList.completed_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {pickingList.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{pickingList.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Pick Items */}
      <Card>
        <CardHeader>
          <CardTitle>Pick Items</CardTitle>
        </CardHeader>
        <CardContent>
          <PickingItemsTable
            items={items}
            onConfirmPick={canPick ? setConfirmPickItem : undefined}
            onReportShortage={canPick ? setShortageItem : undefined}
            onSkip={canPick ? handleSkip : undefined}
            readOnly={!canPick}
          />
        </CardContent>
      </Card>

      {/* Modals */}
      <ConfirmPickModal
        open={!!confirmPickItem}
        onClose={() => setConfirmPickItem(null)}
        onSubmit={handleConfirmPick}
        item={confirmPickItem}
        isLoading={confirmPickMutation.isPending}
      />

      <ReportShortageModal
        open={!!shortageItem}
        onClose={() => setShortageItem(null)}
        onSubmit={handleReportShortage}
        item={shortageItem}
        isLoading={reportShortageMutation.isPending}
      />

      <PromptDialog
        open={showCancelPrompt}
        onOpenChange={setShowCancelPrompt}
        title="Cancel Picking List"
        description={`Are you sure you want to cancel picking list "${pickingList?.picking_number}"?`}
        label="Cancellation reason"
        placeholder="Enter reason (optional)"
        confirmLabel="Cancel Picking"
        onConfirm={handleCancel}
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
